/**
 * The fire panel's model (docs/ux.md §6): who can shoot at a target, how many shots each
 * commits, the odds and what a hit would do. Pure; legality comes from the rules engine.
 */
import {
  indexMap,
  parseCellId,
  rayBetween,
  fireTargets,
  volleyCandidates,
  type GameState,
  type VolleyShooter,
} from '@brig/rules-engine';

/** The panel's choice: shots committed per candidate shooter, 0 = not firing (U-FIRE-04). */
export interface FireDraft {
  readonly target: string;
  readonly shots: Readonly<Record<string, number>>;
}

function unit(state: GameState, id: string) {
  return state.ships.find((s) => s.id === id) ?? state.forts.find((f) => f.id === id);
}

export function shotsLeft(state: GameState, id: string): number {
  return unit(state, id)?.shotsLeft ?? 0;
}

export function isEnemyUnit(state: GameState, id: string): boolean {
  const side = state.turn.side;
  const ship = state.ships.find((s) => s.id === id);
  if (ship) return ship.side !== side && !ship.sunk;
  const fort = state.forts.find((f) => f.id === id);
  return fort !== undefined && fort.side !== side && !fort.destroyed;
}

/** Own units able to fire at `target` now; none unless it is an afloat enemy (U-FIRE-01). */
export function attackersOf(state: GameState, target: string): string[] {
  return isEnemyUnit(state, target) ? volleyCandidates(state, target) : [];
}

/**
 * U-FIRE-01/02: a draft for `target`. From the target every candidate commits all its shots;
 * from a chosen shooter only that one does, the others are listed unchecked.
 */
export function openDraft(state: GameState, target: string, from: string | null = null): FireDraft {
  const shots: Record<string, number> = {};
  for (const id of volleyCandidates(state, target)) {
    shots[id] = from === null || from === id ? shotsLeft(state, id) : 0;
  }
  return { target, shots };
}

/** Who may still shoot, after a shot: clamps to the shots left and drops spent shooters. */
export function refreshDraft(state: GameState, draft: FireDraft): FireDraft | null {
  if (!isEnemyUnit(state, draft.target)) return null;
  const candidates = volleyCandidates(state, draft.target);
  if (candidates.length === 0) return null;
  const shots: Record<string, number> = {};
  for (const id of candidates) shots[id] = Math.min(draft.shots[id] ?? 0, shotsLeft(state, id));
  return { target: draft.target, shots };
}

export function setShots(state: GameState, draft: FireDraft, id: string, n: number): FireDraft {
  if (!(id in draft.shots)) return draft;
  const clamped = Math.max(0, Math.min(shotsLeft(state, id), Math.round(n)));
  return { ...draft, shots: { ...draft.shots, [id]: clamped } };
}

export function committed(draft: FireDraft): VolleyShooter[] {
  return Object.entries(draft.shots)
    .filter(([, shots]) => shots > 0)
    .map(([id, shots]) => ({ id, shots }));
}

export const totalShots = (draft: FireDraft) => committed(draft).reduce((n, s) => n + s.shots, 0);

/** "Fire one by one": every committed shot as its own spin, shooter by shooter (U-FIRE-07). */
export function shotQueue(draft: FireDraft): string[] {
  return committed(draft).flatMap((s) => Array.from({ length: s.shots }, () => s.id));
}

/** Damage still needed to sink the ship or destroy the fort. */
export function toFinish(
  state: GameState,
  target: string,
): { readonly left: number; readonly max: number; readonly now: number } {
  const ship = state.ships.find((s) => s.id === target);
  if (ship) {
    const max = state.ruleset.shipClasses[ship.cls].sinksAt;
    return { left: max - ship.damage, max, now: ship.damage };
  }
  const fort = state.forts.find((f) => f.id === target);
  const max = fort ? state.ruleset.fortClasses[fort.cls].destroyedAt : 0;
  const now = fort?.hits ?? 0;
  return { left: max - now, max, now };
}

export function distance(state: GameState, from: string, to: string): number {
  const a = unit(state, from);
  const b = unit(state, to);
  const pa = a ? parseCellId(a.cell) : null;
  const pb = b ? parseCellId(b.cell) : null;
  return pa && pb ? (rayBetween(pa, pb)?.distance ?? 0) : 0;
}

/** R-PORT-04: a ship firing from a defended port cell must stay there for the rest of the turn. */
export function staysInPort(state: GameState, shooterId: string): boolean {
  const ship = state.ships.find((s) => s.id === shooterId);
  const p = ship ? parseCellId(ship.cell) : null;
  if (!ship || !p || ship.firedInPort) return false;
  const fortId = indexMap(state.map).portOf(p);
  const fort = state.forts.find((f) => f.id === fortId);
  return fort !== undefined && !fort.destroyed;
}

/** Whether the selected own unit can shoot at `target` (U-FIRE-02). */
export function canShootAt(state: GameState, shooter: string | null, target: string): boolean {
  return shooter !== null && shotsLeft(state, shooter) > 0 && fireTargets(state, shooter).includes(target);
}
